import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  BusinessStatus,
  BusinessStatusColor,
} from 'src/app/shared/models/constants/business-status';
import { BusinessCard } from 'src/app/shared/models/dtos/responses/business-card/business-card';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-business-card',
  templateUrl: './business-card.component.html',
  styleUrls: ['./business-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

/**
 * A business card
 */
export class BusinessCardComponent implements AfterContentChecked, OnInit {
  @Input() business?: BusinessCard;
  readonly labels = {
    businessAdmin: 'business-admin',
    createdOn: 'company-account-created-on',
    active: 'active',
    invited: 'invited',
    idle: 'idle',
    disabled: 'disabled',
    series: 'business-series',
    points: 'points',
    total: 'total',
  };
  showInfo = false;
  pointSummary?: PointSummaryDTO;
  loadingPointSummary = false;
  statusColor = '';
  statusText = '';
  language = '';
  selectedFamily = '';

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private readonly rewardService: RewardService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router
  ) {
    this.language = this.translationService.getActiveLang();
  }

  /**
   * ngAfterContentChecked
   */
  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.translationService
      .translateObject(this.labels)
      .then(
        () =>
          ([this.statusColor, this.statusText] = this.setMigrationColorStatus())
      );
  }

  /**
   * Pulls point summary if not available yet
   * @param {number} familyId
   */
  async pullPointSummary(familyId: number) {
    if (!this.loadingPointSummary && this.business != null) {
      this.loadingPointSummary = true;
      this.pointSummary = await firstValueFrom(
        this.rewardService.getBusinessPointSummaryByFamily(
          this.business?.businessId,
          familyId
        )
      );
      this.loadingPointSummary = false;
    }
  }

  /**
   * Toggles show info
   * @param {Event} event
   * @param {number} familyId
   * @param {string} familyName
   */
  toggleInfo(event: Event, familyId: number, familyName: string) {
    event.stopPropagation();
    if (!this.showInfo) {
      this.selectedFamily = familyName;
      this.pullPointSummary(familyId).then();
    }
    this.showInfo = !this.showInfo;
  }

  /**
   * Turns off show info
   */
  closeInfo() {
    this.showInfo = false;
  }

  /**
   * Country flag
   */
  get countryFlag() {
    return 'flag';
  }

  /**
   * Business admin name with description
   */
  get adminName() {
    const name = `${this.business?.businessAdminFirstName} ${this.business?.businessAdminLastName}`;
    return `${name} | ${this.labels.businessAdmin}`;
  }

  /**
   * Business admin series
   */
  get adminSeries() {
    const series = this.business!.businessLevels.length;
    return `${series} ${this.labels.series}`;
  }

  /**
   * First three letters of business country
   */
  get countryShortName() {
    return this.business?.businessCountry.name.substring(0, 3).toUpperCase();
  }

  /**
   * Routes to costumer detail page
   */
  openCustomerDetailPage() {
    this.router.navigate(
      [RoutesKeys.CUSTOMER_DETAIL, this.business?.businessId],
      { queryParams: { detail: 'summary' } }
    );
  }

  /**
   * Returns the migration color according to migration status
   * @return {string[]}
   */
  private setMigrationColorStatus() {
    switch (this.business?.status) {
      case BusinessStatus.INVITED:
        return [BusinessStatusColor.YELLOW, this.labels.invited];
      case BusinessStatus.IDLE:
        return [BusinessStatusColor.RED, this.labels.idle];
      default:
        return [BusinessStatusColor.GREEN, this.labels.active];
    }
  }
}
